import { extractDate } from "@/lib/utils";
import { formatHit } from "@/provider/ai/tools";
import { AiAskRequest, AiAskResponse } from "@/provider/ai/type";
import { OptionsContext } from "@/provider/options-provider";
import { useContext, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { PplxResponse } from "@/provider/perplexity/type";
import { getExternalResults } from "@/app/search-form";

const ThreadQuestion = ({
  setDrawerOpen,
  threadLoading,
  setThreadLoading,
  setResult,
  setInitialQuestion,
  setExtResult,
  setExtResultLoading,
  inputText,
}: {
  drawerOpen: boolean;
  setDrawerOpen: (value: boolean) => void;
  threadLoading: boolean;
  setThreadLoading: (value: boolean) => void;
  result?: AiAskResponse;
  setResult?: (value: AiAskResponse | undefined) => void;
  setInitialQuestion: (value: string) => void;
  extResult?: undefined | null | PplxResponse;
  setExtResult: (value: PplxResponse | null) => void;
  extResultLoading: boolean;
  setExtResultLoading: (value: boolean) => void;
  inputText?: string;
}) => {
  const {
    searchOptions,
    setSearchOptions,
    groupSummaryItems,
    searchResults,
    searchText,
  } = useContext(OptionsContext);
  const [useSummary, setUseSummary] = useState(false);

  const askNew = async () => {
    if (!searchOptions.question || searchOptions.question === "") {
      return;
    }

    try {
      setThreadLoading(true);
      setResult?.(undefined);
      setExtResult(null);
      let data: AiAskResponse;
      const ids = groupSummaryItems.map((item) => `${item.type}${item.id}`);

      const inputText =
        searchResults
          ?.filter((i) => groupSummaryItems.find((g) => g.id === i.id))
          ?.map((r) => formatHit(r))
          .join("\n") ?? "";

      if (ids.length === 0) return;

      const question: AiAskRequest = {
        system_prompt: searchOptions.systemPrompt,
        prompt: searchOptions.question,
        event_type: "Q_AND_A_SEARCH",
        ids: ids,
        use_summary: useSummary,
        input_text: inputText,
        keywords: [searchText],
        ticker: searchOptions.ticker,
        period: `${extractDate(searchOptions.dateRange?.from)} to ${extractDate(
          searchOptions.dateRange?.to,
        )}`,
      };

      if (searchText && searchText !== "") {
        question.input_text = inputText;
      }

      const response = await fetch("/api/ask", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(question),
      });
      setThreadLoading(false);

      data = await response.json();
      setInitialQuestion(searchOptions.question);
      setResult?.(data);
      setExtResultLoading(true);
      const context: string[] = question.keywords ?? [];
      if (searchOptions.ticker) context.push(searchOptions.ticker);
      await getExternalResults(
        question.prompt,
        searchOptions.ticker,
        context,
        setExtResult,
        setExtResultLoading,
        data.id,
      );
      setExtResultLoading(false);
      setSearchOptions({ ...searchOptions, question: "" });
    } catch (error) {
      setThreadLoading(false);
      setExtResultLoading(false);
      setResult?.(undefined);
      setExtResult(null);
    }
  };

  return (
    <div className="flex w-full flex-col">
      <div className="flex w-full flex-row items-center">
        <Input
          value={searchOptions.question}
          onChange={(e) =>
            setSearchOptions({ ...searchOptions, question: e.target.value })
          }
          title="Type the question and enter to ask a question"
          autoComplete="off"
          placeholder="Type a question and hit enter..."
          className="h-16 w-full rounded-r-none bg-white font-semibold focus-visible:ring-0"
          type="search"
          disabled={threadLoading}
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              await askNew();
            }
          }}
        />
        <Button
          onClick={async () => {
            setThreadLoading(true);
            setDrawerOpen(true);
            await askNew();
          }}
          className="h-16 rounded-l-none"
          disabled={threadLoading}
        >
          Ask
        </Button>
      </div>
      <div className="mt-1 flex flex-row items-center space-x-2">
        <Switch
          className="scale-75"
          checked={useSummary}
          onCheckedChange={() => setUseSummary(!useSummary)}
          disabled={threadLoading}
        ></Switch>
        <Label className="text-xs text-black">
          Use summaries instead of first pages.
        </Label>
      </div>
    </div>
  );
};

export default ThreadQuestion;
